import { withNamespaces } from "react-i18next";
import { Alert, Button, Col, Label, Row, Spinner } from "reactstrap";
import { useEffect, useRef, useState } from "react";
import ConfluenceService from "src/modules/data-protection/apis/ConfluenceService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AvField, AvForm } from "availity-reactstrap-validation";
import ToggleActivationModal from "./components/ToggleActivationModal";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const ConfluenceSettings = (props) => {
    const { t } = props;
    const formRef = useRef();
    const [initialSettings, setInitialSettings] = useState();
    const [defaultSettings, setDefaultSettings] = useState({});
    const [testSuccess, setTestSuccess] = useState(null);
    const [active, setActive] = useState(false);
    const [showToggleActivationModal, setShowToggleActivationModal] = useState(false);
    const service = ConfluenceService.getInstance();

    const {
        isFetching: isFetchingSettings
    } = useQuery({
        queryKey: ["data-protection-fetch-confluence-settings"],
        queryFn: async () => {
            return await service.fetchSettings();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            setInitialSettings(data);
            setDefaultSettings(data);
            setActive(data?.isActive);
        },
        onError: () => {
            toast(props.t("An error occurred while fetching settings."), {
                type: "error"
            });
        },
    });

    const submitMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.saveSettings(payload);
        },
        onSuccess: () => {
            toast.success(t("Settings saved successfully."));
            if(!active) {
                setShowToggleActivationModal('activation_after_save');
            }
        },
        onError: () => {
            toast.error(t("An error occurred while saving settings."));
        },
    });

    const toggleActivationMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.toggleActivation(payload);
        },
        onSuccess: () => {
            toast.success(t(`Settings ${active ? 'deactivated' : 'activated'} successfully.`));
            setActive(!active);
        },
        onError: () => {
            toast.error(t("An error occurred while toggling activation."));
        },
    });

    const testConnectionMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.testConnection(payload);
        },
        onSuccess: () => {
            toast.success(t('Confluence connection test successful.'));
            setTestSuccess(true);
        },
        onError: () => {
            toast.error(t("Confluence connection test failed. Please check your configuration and try again."));
            setTestSuccess(false);
        }
    });

    useEffect(() => {
        if(!defaultSettings && initialSettings) {
            setDefaultSettings(initialSettings);
        }
    }, [ defaultSettings, initialSettings ]);

    if (!isFeatureEnabledForActiveModule('confluence_setting')) {
        return (
            <Alert
                color="warning"
            >
                {t("Please upgrade your plan to access this feature")}.
            </Alert>
        );
    }

    const handleSubmit = (_e, values) => {
        submitMutation.mutate(values);
    };

    const handleTestConnection = () => {
        testConnectionMutation.mutate(formRef.current.getValues());
    }

    const handleRestore = () => {
        setDefaultSettings(null)
    };

    const isFirstTime = !initialSettings?.baseUrl && 
                        !initialSettings?.email && 
                        !initialSettings?.spaceKey;

    return (
        <Row>
            <Col sm="12" md='12' xl="9">
                {!isFirstTime && (
                    <Alert 
                        color={active ? 'success' : 'warning'} 
                        className="mb-4"
                    >
                        {active ? (
                            <>
                                <i className="ri-checkbox-circle-fill me-1 font-size-22 align-middle" /> 
                                {t('Confluence integration is active. The system is synchronizing with your Confluence workspace.')}
                            </>
                        ) : (
                            <>
                                <i className="ri-error-warning-fill me-1 font-size-22 align-middle" />
                                {t('Confluence integration is currently disabled. Activate it to enable synchronization with your Confluence workspace.')}
                            </>
                        )}
                    </Alert>
                )}

                <AvForm
                    className="needs-validation"
                    onValidSubmit={handleSubmit}
                    ref={formRef}
                >
                    <Row>
                        <Col sm="12" md="6" className="mb-3">
                            <Label for="baseUrl">
                                {t("Confluence Base URL")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="baseUrl"
                                    name="baseUrl"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true },
                                        pattern: {
                                            value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                            errorMessage: t("Please enter a valid URL"),
                                        }
                                    }}
                                    value={defaultSettings?.baseUrl || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="email">
                                {t("Atlassian Account Email")}
                            </Label>
                            {isFetchingSettings ? (
                                <div 
                                    className="dt-field dt-skeleton" 
                                    style={{width: '100%'}} 
                                />
                            ) : (
                                <AvField
                                    id="email"
                                    name="email"
                                    type="email"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: {
                                            value: true,
                                            errorMessage: t("This field cannot be blank"),
                                        },
                                        email: { errorMessage: t("Your email is invalid") },
                                    }}
                                    value={defaultSettings?.email || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="apiToken">
                                {t("API Token")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="apiToken"
                                    name="apiToken"
                                    type="password"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.apiToken || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="spaceKey">
                                {t("Confluence Space Key")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="spaceKey"
                                    name="spaceKey"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.spaceKey || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" className="text-end">
                            <div className="d-inline-block">
                                <div>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={isFetchingSettings || submitMutation.isLoading}
                                        className="me-2"
                                    >
                                        {submitMutation.isLoading && (
                                            <Spinner
                                                size="sm"
                                                color="priamry"
                                                className="me-2"
                                            />
                                        )}
                                        {t("Save Settings")}
                                    </Button>

                                    {!isFirstTime && (
                                        <>
                                            <Button
                                                type="button"
                                                color="info"
                                                className="me-2"
                                                onClick={handleTestConnection}
                                                disabled={isFetchingSettings || submitMutation.isLoading || testConnectionMutation.isLoading}
                                            >
                                                {testConnectionMutation.isLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        color="white"
                                                        className="me-2"
                                                    />
                                                )}
                                                {t("Test Connection")}
                                            </Button>

                                            <Button
                                                type="button"
                                                color={active ? 'danger' : 'success'}
                                                className="me-2"
                                                onClick={() => setShowToggleActivationModal(active ? 'deactivate' : 'activate')}
                                                disabled={isFetchingSettings || submitMutation.isLoading}
                                            >
                                                {active ? (
                                                    t("Deactivate Confluence Integration")
                                                ) : (
                                                    t("Activate Confluence Integration")
                                                )}
                                            </Button>
                                        </>
                                    )}

                                    <Button
                                        color="secondary"
                                        type="button"
                                        onClick={handleRestore}
                                        disabled={isFetchingSettings || submitMutation.isLoading}
                                    >
                                        {t("Restore Defaults")}
                                    </Button>
                                </div>
                                <div className="text-start mt-1">
                                    {testSuccess === true && (
                                        <span className="text-success">
                                            <i className="ri-check-line align-middle font-size-18"></i>
                                            {t("Connection successful. Your Confluence settings are valid.")}
                                        </span>
                                    )}
                                    {testSuccess === false && (
                                        <span className="text-danger">
                                            <i className="ri-error-warning-line align-middle font-size-18"></i>
                                            {t("Connection failed: invalid credentials.")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </AvForm>

                <ToggleActivationModal 
                    isOpen={showToggleActivationModal !== false}
                    toggle={() => {
                        setShowToggleActivationModal(false)
                    }}
                    toggleActivationMutation={toggleActivationMutation}
                    action={showToggleActivationModal}
                />
            </Col>
        </Row>
    );
};

export default withNamespaces()(ConfluenceSettings);
